import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
    Card,
    CardBody,
    CardHeader,
    Typography,
    Button,
} from "@material-tailwind/react";
import ContentWrapper from "components/ui/content-wrapper";
import useSWR from "swr";
import { fetcherFull, fetcherSimple } from "utils/api";
import { toast } from "react-toastify";
import getStripe from "utils/getStripe";
import {
    EmbeddedCheckout,
    EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { useDispatch, useSelector } from "react-redux";
import {
    checkUserData,
    fetchPurchases,
    fetchCredits,
    fetchOrgStats,
    userSelector,
    canReadPurchases,
} from "store/user";

export default function Payment() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector(userSelector);

    const [searchParams, setSearchParams] = useSearchParams();
    const [productPurchased, setProductPurchased] = useState(false);
    const [paymentFailed, setPaymentFailed] = useState(false);
    // Guard against accidental double-invocation of fetchClientSecret
    // (e.g. React StrictMode double-effects or rapid navigation). This is
    // client-side only; see TODO below about backend idempotency.
    const submittingRef = useRef(false);

    const {
        data: sessionStatus,
        error: sessionStatusError,
        mutate: sessionStatusMutate,
        isValidating: sessionStatusIsValidating,
    } = useSWR(
        [
            searchParams.has("session_id")
                ? `/v2/payment/stripe/checkout-session-status?session_id=${searchParams.get("session_id")}`
                : null,
            "GET",
        ],
        ([path, method]) => fetcherSimple(path, method),
    );

// Purchases refresh, deferred until @me resolves: the session-status
    // SWR can settle first on a hard load, and the guard needs the
    // user's permissions. Fires once per completed checkout, only for
    // users who can read the endpoint.
    const purchasesFiredRef = useRef(false);
    useEffect(() => {
        if (purchasesFiredRef.current) return;
        if (sessionStatus?.status !== "complete") return;
        if (!user.userData) return;
        purchasesFiredRef.current = true;
        if (canReadPurchases(user.userData)) {
            dispatch(fetchPurchases());
        }
    }, [sessionStatus, user.userData, dispatch]);

    useEffect(() => {
        if (sessionStatus) {
            if (sessionStatusError) {
                setPaymentFailed(true);
            } else if (sessionStatus?.status === "open") {
                setPaymentFailed(false);
                setProductPurchased(false);
            } else if (sessionStatus?.status === "complete") {
                dispatch(checkUserData());
                dispatch(fetchCredits());
                dispatch(fetchOrgStats());
                setProductPurchased(true);
                setPaymentFailed(false);
            } else {
                setPaymentFailed(true);
            }
        }
    }, [sessionStatus, sessionStatusError]);

    const fetchClientSecret = useCallback(() => {
        const tobuySku = searchParams.get("product_sku");
        if (!tobuySku) {
            return;
        }
        if (submittingRef.current) return;
        submittingRef.current = true;
        let basket_to_buy = { basket: [{ sku: tobuySku, quantity: 1 }] };
        // Create a Checkout Session
        return fetcherFull(
            "/v2/payment/stripe/create-checkout-session",
            "POST",
            basket_to_buy,
        )
            .then((data) => data.client_secret)
            .catch((errorResponse) => {
                // toast.error expects a string (or ToastContent). The previous
                // implementation passed a config object, which is the shape
                // used by toast.update(), causing toast.error to render
                // "[object Object]" (or nothing) and silently drop the error.
                const message =
                    errorResponse?.error ||
                    errorResponse ||
                    "unknown error";
                toast.error(`Checkout Failure, ${message}`);
            })
            .finally(() => {
                submittingRef.current = false;
            });
        // TODO(idempotency): backend needs to (1) accept Idempotency-Key from
        // frontend, (2) forward it on the outgoing Stripe request at
        // Payment.pm:138, (3) store the key -> response mapping for its own
        // retries. Tracked in ../gobbyapi. Client-side submittingRef only
        // prevents accidental double-click; does not protect against network
        // retry or React StrictMode double-invoke.
    }, [searchParams]);

    const options = { fetchClientSecret };

    // Determine what message to show
    const getPaymentMessage = () => {
        if (paymentFailed) {
            return "Sorry your purchase was unsuccessful";
        }

        if (productPurchased) {
            const productInfo = sessionStatus?.product_info;
            if (productInfo?.description) {
                return `Thank you for your purchase of ${productInfo.description}`;
            } else if (productInfo?.short_name) {
                return `Thank you for your purchase of ${productInfo.short_name}`;
            } else {
                return "Thank you for your purchase";
            }
        }

        return null;
    };

    return (
        <>
            <ContentWrapper>
                <div className="mb-10 text-start rounded-xl lg:p-6 p-4">
                    <div className="mt-4 mx-auto">
                        {/* Show payment status card when payment is complete or failed */}
                        {(productPurchased || paymentFailed) && (
                            <Card className="card rounded-2xl py-4 px-4 w-full md:w-3/4 lg:max-w-2xl mx-auto">
                                <CardHeader
                                    floated={false}
                                    shadow={false}
                                    className="card-inner-header w-full m-0 text-lg font-display pb-2"
                                >
                                    <Typography variant="h2" className="card-header-text text-center">
                                        {paymentFailed ? "Payment Unsuccessful" : "Payment Successful"}
                                    </Typography>
                                </CardHeader>
                                <CardBody className="card-inner-body">
                                    <Typography
                                        variant="h5"
                                        className="mb-6 text-2xl font-semibold text-center"
                                    >
                                        {getPaymentMessage()}
                                    </Typography>
                                    <div className="flex justify-center">
                                        <Button
                                            onClick={() => navigate('/?payment_success=true')}
                                            className="btn btn--cta"
                                            variant="filled"
                                            shadow={false}
                                            size="lg"
                                        >
                                            Return to Dashboard
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {/* Show Stripe checkout form for initial payment */}
                        {!!getStripe() &&
                            !productPurchased &&
                            !paymentFailed &&
                            searchParams.get("product_sku") && (
                            <Card className="card rounded-2xl py-4 px-4 w-full md:w-3/4 mx-auto">
                                <CardHeader
                                    floated={false}
                                    shadow={false}
                                    className="card-inner-header w-full m-0 text-lg font-display pb-2"
                                >
                                    <Typography variant="h4" className="card-header-text">
                                        Complete Your Purchase
                                    </Typography>
                                </CardHeader>
                                <CardBody className="card-inner-body">
                                    <div className="mb-4 flex justify-start">
                                        <Button
                                            onClick={() => navigate(-1)}
                                            className="btn btn--secondary"
                                            variant="outlined"
                                            size="sm"
                                        >
                                            Return
                                        </Button>
                                    </div>
                                    <div id="checkout">
                                        <EmbeddedCheckoutProvider
                                            stripe={getStripe()}
                                            options={options}
                                        >
                                            <EmbeddedCheckout />
                                        </EmbeddedCheckoutProvider>
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {/* Show error if Stripe fails to load */}
                        {!getStripe() && (
                            <Card className="card rounded-2xl py-4 px-4">
                                <CardHeader
                                    floated={false}
                                    shadow={false}
                                    className="card-inner-header w-full m-0 text-lg font-display pb-2"
                                >
                                    <Typography variant="h4" className="card-header-text">
                                        Payment System Error
                                    </Typography>
                                </CardHeader>
                                <CardBody className="card-inner-body">
                                    <Typography className="text-red-600">
                                        Error loading payment system!
                                    </Typography>
                                </CardBody>
                            </Card>
                        )}
                    </div>
                </div>
            </ContentWrapper>
        </>
    );
}
